import { z } from 'zod';
import { emailRichTextDocSchema, normalizeEmailRichText, } from './emailRichText.js';
import { paginationSchema, timezoneSchema, uuidSchema } from './schemas.js';
/** 12 is the largest cap at which the heaviest digest the assembler can emit stays clear of Gmail's 102 KB clip, proven by the size gate in packages/emails. */
export const NEWSLETTER_SECTION_MAX = 12;
export const NEWSLETTER_MOST_WATCHED_MAX = 10;
export const NEWSLETTER_SEASONS_PER_SHOW_MAX = 8;
export const NEWSLETTER_WINDOW_MAX_DAYS = 31;
export const NEWSLETTER_EXTRA_ADDRESSES_MAX = 200;
export const NEWSLETTER_EXCLUDED_USERS_MAX = 200;
export const NEWSLETTER_SENDER_NAME_MAX = 100;
export const NEWSLETTER_SEND_TRIGGERS = ['schedule', 'manual', 'test'];
export const NEWSLETTER_SEND_OUTCOMES = [
    'rendering',
    'sending',
    'sent',
    'partial',
    'failed',
    'skipped_empty',
];
export const NEWSLETTER_RECIPIENT_STATUSES = [
    'queued',
    'sent',
    'failed',
    'suppressed',
    'unknown',
];
export const NEWSLETTER_IMAGE_MODES = ['auto', 'hosted', 'inline', 'none'];
export const EMAIL_SUPPRESSION_REASONS = ['unsubscribed', 'manual'];
export const DEFAULT_NEWSLETTER_SUBJECT = "What's new on {{server_name}} ({{end_date}})";
const address = z
    .email()
    .max(254)
    .transform((v) => v.trim().toLowerCase());
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:MM');
/** Five fields of digits and the * , - / operators; BullMQ's parser validates the rest at schedule time. */
const CRON_FIELD = /^[\d*,\-/]+$/;
export const cronExpressionSchema = z
    .string()
    .trim()
    .refine((v) => {
    const fields = v.split(/\s+/);
    return fields.length === 5 && fields.every((f) => CRON_FIELD.test(f));
}, 'Cron needs five fields made of digits, *, comma, dash and slash');
export const newsletterScheduleSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('daily'), time }),
    z.strictObject({ kind: z.literal('weekly'), dayOfWeek: z.number().int().min(0).max(6), time }),
    z.strictObject({ kind: z.literal('monthly'), dayOfMonth: z.number().int().min(1).max(28), time }),
    z.strictObject({ kind: z.literal('cron'), expression: cronExpressionSchema }),
]);
const days = z.number().int().min(1).max(NEWSLETTER_WINDOW_MAX_DAYS);
export const newsletterWindowSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('since_last_send'), fallbackDays: days }),
    z.strictObject({ kind: z.literal('fixed'), days }),
]);
const cap = z.number().int().min(1).max(NEWSLETTER_SECTION_MAX);
export const newsletterSectionsSchema = z.strictObject({
    movies: z.strictObject({ enabled: z.boolean(), max: cap }),
    shows: z.strictObject({
        enabled: z.boolean(),
        max: cap,
        maxSeasonsPerShow: z.number().int().min(1).max(NEWSLETTER_SEASONS_PER_SHOW_MAX),
    }),
    music: z.strictObject({ enabled: z.boolean(), max: cap }),
    mostWatched: z.strictObject({
        enabled: z.boolean(),
        max: z.number().int().min(1).max(NEWSLETTER_MOST_WATCHED_MAX),
    }),
});
export const DEFAULT_NEWSLETTER_SECTIONS = {
    movies: { enabled: true, max: 12 },
    shows: { enabled: true, max: 12, maxSeasonsPerShow: 8 },
    music: { enabled: true, max: 8 },
    mostWatched: { enabled: false, max: 10 },
};
export const NEWSLETTER_SCOPE_LIBRARIES_MAX = 200;
/** A library id is only unique with its server: Plex section ids start at 1 on every server. */
export const newsletterScopeLibrarySchema = z.strictObject({
    serverId: uuidSchema,
    libraryId: z.string().min(1).max(100),
});
export const newsletterScopeSchema = z.strictObject({
    serverIds: z.array(uuidSchema).max(50).default([]),
    libraries: z.array(newsletterScopeLibrarySchema).max(NEWSLETTER_SCOPE_LIBRARIES_MAX).default([]),
});
export const newsletterRecipientsSchema = z.strictObject({
    members: z.boolean().default(true),
    extraAddresses: z
        .array(z.strictObject({ address, name: z.string().trim().max(100).optional() }))
        .max(NEWSLETTER_EXTRA_ADDRESSES_MAX)
        .default([]),
    excludeUserIds: z.array(uuidSchema).max(NEWSLETTER_EXCLUDED_USERS_MAX).default([]),
});
export const newsletterLinksSchema = z.strictObject({ tracearr: z.boolean() });
export const DEFAULT_NEWSLETTER_LINKS = { tracearr: false };
const richTextField = emailRichTextDocSchema
    .nullable()
    .transform((doc) => normalizeEmailRichText(doc))
    .default(null);
export const createNewsletterSchema = z.strictObject({
    name: z.string().trim().min(1).max(100),
    enabled: z.boolean().default(true),
    destinationId: uuidSchema.nullable().default(null),
    schedule: newsletterScheduleSchema,
    timezone: timezoneSchema.unwrap(),
    window: newsletterWindowSchema.default({ kind: 'since_last_send', fallbackDays: 7 }),
    scope: newsletterScopeSchema.default({ serverIds: [], libraries: [] }),
    sections: newsletterSectionsSchema.default(DEFAULT_NEWSLETTER_SECTIONS),
    subject: z.string().trim().min(1).max(200).default(DEFAULT_NEWSLETTER_SUBJECT),
    senderName: z.string().trim().min(1).max(NEWSLETTER_SENDER_NAME_MAX).nullable().default(null),
    intro: richTextField,
    outro: richTextField,
    recipients: newsletterRecipientsSchema.default({
        members: true,
        extraAddresses: [],
        excludeUserIds: [],
    }),
    imageMode: z.enum(NEWSLETTER_IMAGE_MODES).default('auto'),
    skipWhenEmpty: z.boolean().default(true),
    links: newsletterLinksSchema.default(DEFAULT_NEWSLETTER_LINKS),
});
function partialWithoutDefaults(shape) {
    return Object.fromEntries(Object.entries(shape).map(([key, field]) => [
        key,
        (field instanceof z.ZodDefault
            ? field.unwrap()
            : field).optional(),
    ]));
}
/** A PATCH body: absent keys stay absent instead of resetting to the create defaults. */
export const updateNewsletterSchema = z.strictObject(partialWithoutDefaults(createNewsletterSchema.shape));
/** The set of a newsletter's servers a person belongs to, as one string: the ids deduped, sorted and joined by a comma. */
export function variantKey(serverIds) {
    return [...new Set(serverIds)].sort().join(',');
}
/** The scoped servers a member has an account on, in the scope's order; every scoped server for null, an extra address that belongs to no server. */
export function variantServerIds(memberServerIds, scopedServerIds) {
    if (memberServerIds === null)
        return [...scopedServerIds];
    return scopedServerIds.filter((id) => memberServerIds.includes(id));
}
/** A digest that covers several servers has no natural sender name, so the editor asks for one before the Tracearr fallback could fire. */
export function needsSenderName(senderName, scopedServerCount) {
    return !senderName && scopedServerCount > 1;
}
/** The shape variantKey() produces; anything else is refused before a send looks the variant up. */
export const variantKeySchema = z
    .string()
    .max(50 * 37)
    .refine((key) => key.split(',').every((id) => uuidSchema.safeParse(id).success) &&
    variantKey(key.split(',')) === key, 'variantKey must be sorted, comma-separated server ids');
export const newsletterTestSendSchema = z.strictObject({
    address,
    variantKey: variantKeySchema.optional(),
});
/** POST /newsletters/preview: the form as the create route would receive it, plus the saved row's id when editing so the window starts at its watermark. */
export const newsletterPreviewDraftSchema = z.strictObject({
    newsletterId: uuidSchema.optional(),
    newsletter: createNewsletterSchema,
});
/** POST /newsletters/recipients: only the scope and the recipients decide who is listed; the saved row's id marks members its last send missed. */
export const newsletterRecipientsDraftSchema = z.strictObject({
    newsletterId: uuidSchema.optional(),
    scope: newsletterScopeSchema,
    recipients: newsletterRecipientsSchema,
});
export const emailSuppressionCreateSchema = z.strictObject({ address });
export const newsletterSendsQuerySchema = paginationSchema;
export const EMAIL_LOGO_MODES = ['tracearr', 'none', 'url'];
/** The web palette's primary as hex; packages/emails ships the same value as DEFAULT_ACCENT. */
const DEFAULT_ACCENT_COLOR = '#0ea0b3';
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a hex color like #0ea0b3');
/** z.url() strips embedded CR/LF and parses what's left, so control characters are rejected before the URL parse ever sees them. */
const logoUrl = z
    .string()
    // eslint-disable-next-line no-control-regex -- matching control characters is the point of this check
    .refine((value) => !/[\u0000-\u001f\u007f]/.test(value), {
    message: 'URL must not contain control characters',
})
    .pipe(z.url({ protocol: /^https?$/ }).max(500));
export const emailBrandingSchema = z.strictObject({
    logo: z
        .discriminatedUnion('mode', [
        z.strictObject({ mode: z.literal('tracearr') }),
        z.strictObject({ mode: z.literal('none') }),
        z.strictObject({ mode: z.literal('url'), url: logoUrl }),
    ])
        .default({ mode: 'tracearr' }),
    accentColor: hexColor.default(DEFAULT_ACCENT_COLOR),
    /** Header name beside the logo. Null keeps the per-send name (the server or newsletter). */
    systemTitle: z.string().trim().max(120).nullable().default(null),
    footerText: z.string().trim().max(500).nullable().default(null),
    postalAddress: z.string().trim().max(500).nullable().default(null),
    mailtoUnsubscribe: z.boolean().default(false),
});
export const DEFAULT_EMAIL_BRANDING = emailBrandingSchema.parse({});
/** A block an older build wrote may carry keys this one dropped; the read path strips them instead of resetting to defaults. */
export const emailBrandingReadSchema = z.object(emailBrandingSchema.shape);
export const NEWSLETTER_VIEW_TOKEN_LENGTH = 43;
export const NEWSLETTER_SNAPSHOT_RETENTION_DAYS = 90;
export const NEWSLETTER_SEND_RETENTION_DAYS = 365;
export function newsletterCron(schedule) {
    if (schedule.kind === 'cron')
        return schedule.expression;
    const [hh, mm] = schedule.time.split(':').map(Number);
    switch (schedule.kind) {
        case 'daily':
            return `${mm} ${hh} * * *`;
        case 'weekly':
            return `${mm} ${hh} * * ${schedule.dayOfWeek}`;
        case 'monthly':
            return `${mm} ${hh} ${schedule.dayOfMonth} * *`;
    }
}
/** Null means the scoped server's name when the scope resolves to exactly one server; several servers have no natural name. */
export function resolveSenderName(senderName, scopedServerNames) {
    if (senderName)
        return senderName;
    const [only] = scopedServerNames;
    return scopedServerNames.length === 1 && only !== undefined ? only : 'Tracearr';
}
export const NEWSLETTER_EXCLUDED_REASONS = ['excluded', 'banned', 'pending', 'noServer'];
//# sourceMappingURL=newsletters.js.map